import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../hooks/useAuth.jsx';
import { useBranding } from '../../../hooks/useBranding.js';
import { useCardSettings, useCardSettingsMutations } from '../../../hooks/useCardSettings.js';
import { useCardLayout } from '../../../hooks/useCardLayout.js';
import { BannerCropper } from '../../../components/cards/BannerCropper.jsx';
import { KonvaCardPreview } from '../../../components/cards/KonvaCardPreview.jsx';
import { CardEditorModal, CardEditorTrigger } from '../../../components/cardeditor/CardEditorModal.jsx';
import { generateCardPdf } from '../../../lib/cards';
import { ApiError } from '../../../lib/api.js';

/**
 * ID Cards settings page.
 *
 * Two columns: form left, live preview right. Matches the Branding
 * page's layout convention.
 *
 * Reads tenant branding via useBranding() (for logo, brand colour,
 * display name mode, etc) and merges with card-specific fields from
 * useCardSettings(). The merged shape is what gets passed to the
 * preview component AND the generateCardPdf sample button.
 *
 * Knobs are Premium-only — shown disabled with a hint when Minimal
 * is selected, to keep the surface predictable (no fields disappearing
 * on template switch).
 */

const TEMPLATES = [
  {
    key: 'minimal',
    label: 'Minimal',
    description: 'Clean, compact. Wave band at the bottom.',
  },
  {
    key: 'premium',
    label: 'Premium',
    description: 'Portrait photo, feature imagery, tune-ables.',
  },
];

const PHOTO_SIZES = [
  { key: 'small', label: 'Small' },
  { key: 'medium', label: 'Medium' },
  { key: 'large', label: 'Large' },
];

const NAME_FONT_SIZES = [
  { key: 'small', label: 'Small' },
  { key: 'medium', label: 'Medium' },
  { key: 'large', label: 'Large' },
];

const FOOTER_TEXT_COLORS = [
  { key: 'auto', label: 'Auto (contrast)' },
  { key: 'white', label: 'White' },
  { key: 'dark', label: 'Dark' },
  { key: 'brand', label: 'Brand tint' },
];

export function IdCardsPage() {
  const { tenant } = useAuth();
  const { branding } = useBranding();
  const { card, loading, error, refresh } = useCardSettings();
  const { layout: cardLayout, layout: savedLayout, refresh: refreshLayout } = useCardLayout();
  const { updateCardSettings, uploadBottomImage, deleteBottomImage } = useCardSettingsMutations();

  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState({
    template: 'minimal',
    company_name: '',
    photo_size: 'medium',
    name_font_size: 'medium',
    footer_text_color: 'auto',
    banner_opacity: 100,
  });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [downloadingSample, setDownloadingSample] = useState(false);

  // Hydrate form from loaded card settings
  useEffect(() => {
    if (card) {
      setForm({
        template: card.template || 'minimal',
        company_name: card.company_name || '',
        photo_size: card.photo_size || 'medium',
        name_font_size: card.name_font_size || 'medium',
        footer_text_color: card.footer_text_color || 'auto',
        banner_opacity: typeof card.banner_opacity === 'number' ? card.banner_opacity : 100,
      });
      setDirty(false);
    }
  }, [card]);

  useEffect(() => {
    if (!dirty) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  function setField(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    setDirty(true);
    setSaveSuccess(false);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaveError(null);
    setSaveSuccess(false);

    setSaving(true);
    try {
      await updateCardSettings({
        template: form.template,
        company_name: form.company_name || null,
        photo_size: form.photo_size,
        name_font_size: form.name_font_size,
        footer_text_color: form.footer_text_color,
        banner_opacity: Number(form.banner_opacity),
      });
      setDirty(false);
      setSaveSuccess(true);
      refresh();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleBottomImageUpload(file) {
    try {
      await uploadBottomImage(file);
      refresh();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Banner upload failed.');
    }
  }

  async function handleBottomImageDelete() {
    try {
      await deleteBottomImage();
      refresh();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Banner delete failed.');
    }
  }

  async function handleDownloadSample() {
    setDownloadingSample(true);
    try {
      const sampleStaff = {
        first_name: 'Dani',
        last_name: 'Morgan',
        role: 'Senior Cleaner',
        photo_url: null,
      };
      const mergedTenant = { ...previewTenant, card_layout_json: savedLayout || null };
      const pdf = await generateCardPdf({
        staff: sampleStaff,
        tenant: mergedTenant,
        staffSlug: 'sample',
      });
      pdf.save('sample-id-card.pdf');
    } catch (err) {
      console.error('Sample download failed:', err);
      setSaveError('Sample PDF generation failed. Check console.');
    } finally {
      setDownloadingSample(false);
    }
  }

  // Merged tenant shape for preview — live form values override saved card settings,
  // branding provides the logo/brand colour/name mode context.
  const previewTenant = useMemo(() => ({
    // Identity
    name: tenant?.name,
    slug: tenant?.slug,
    // Branding (read-only here, edited on Branding page)
    logo_url: branding?.logo_url,
    brand_color: branding?.brand_color || '#00AB39',
    display_name_mode: branding?.display_name_mode || 'full',
    public_phone: branding?.public_phone,
    public_website: branding?.public_website,
    public_tagline: branding?.public_tagline,
    // Card-specific (live form values)
    card_template: form.template,
    card_company_name: form.company_name || null,
    card_bottom_image_url: card?.bottom_image_url || null,
    card_photo_size: form.photo_size,
    card_name_font_size: form.name_font_size,
    card_footer_text_color: form.footer_text_color,
    card_banner_opacity: Number(form.banner_opacity),
  }), [tenant, branding, form, card]);

  const isPremium = form.template === 'premium';

  if (loading) {
    return (
      <div style={{ padding: 'var(--sp-10) 0', textAlign: 'center' }}>
        <div className="spinner" aria-hidden="true" />
        <p className="muted" style={{ marginTop: 'var(--sp-3)' }}>Loading card settings…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error" role="alert">
        <strong>Couldn't load card settings.</strong>{' '}
        <button type="button" className="btn btn-ghost" onClick={refresh} style={{ padding: 0, textDecoration: 'underline' }}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="page-header">
        <div className="page-title-wrap">
          <h1 className="page-title">ID Cards</h1>
          <p className="page-subtitle">
            Design the staff ID cards you'll print for your team. Updates
            apply instantly — no need to regenerate old cards.
          </p>
        </div>
      </div>

      {saveError && <div className="alert alert-error" role="alert">{saveError}</div>}
      {saveSuccess && (
        <div className="alert alert-success" role="status">
          Card settings saved.
        </div>
      )}

      <div className="branding-layout">
        <form onSubmit={handleSave} className="branding-form-card" noValidate>

          {/* Template selector */}
          <fieldset
            className="editor-section"
            style={{ border: 0, padding: 0, margin: 0, marginBottom: 'var(--sp-6)' }}
          >
            <legend className="editor-section-title">Template</legend>
            <div className="field-hint" style={{ marginBottom: 'var(--sp-3)' }}>
              Pick the starting preset for the card editor. If you have a custom
              layout, you can change the backdrop (wave vs mountain/banner)
              directly from the editor&apos;s toolbar — this setting doesn&apos;t affect it.
            </div>
            <div className="display-mode-options" role="radiogroup" aria-label="Card template">
              {TEMPLATES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  role="radio"
                  aria-checked={form.template === t.key}
                  aria-pressed={form.template === t.key}
                  className="display-mode-option"
                  onClick={() => setField('template', t.key)}
                >
                  <h4 className="display-mode-label">{t.label}</h4>
                  <p className="display-mode-example">{t.description}</p>
                </button>
              ))}
            </div>
          </fieldset>

          {/* Company name on card */}
          <fieldset className="editor-section" style={{ border: 0, padding: 0, margin: 0, marginBottom: 'var(--sp-6)' }}>
            <legend className="editor-section-title">Company name on card</legend>
            <div className="field">
              <div className="field-hint">
                Text that appears when no logo renders, or as the title on
                Premium. Defaults to your organisation name if empty.
              </div>
              <input
                className="field-input"
                type="text"
                value={form.company_name}
                onChange={(e) => setField('company_name', e.target.value)}
                placeholder={tenant?.name || 'Your Company'}
                maxLength={40}
              />
              <div className="field-hint" style={{ marginTop: 'var(--sp-1)', textAlign: 'right' }}>
                {form.company_name.length}/40
              </div>
            </div>
          </fieldset>

          {/* Bottom banner image (Premium only — but shown always so surface is predictable) */}
          <fieldset className="editor-section" style={{ border: 0, padding: 0, margin: 0, marginBottom: 'var(--sp-6)' }}>
            <legend className="editor-section-title">Bottom banner image</legend>
            <div className="field-hint" style={{ marginBottom: 'var(--sp-3)' }}>
              {isPremium
                ? 'Shown behind the footer on Premium cards. 5:1 ratio recommended, max 2MB. Leave empty to use the default mountain range tinted from your brand colour.'
                : 'Only used by the Premium template. Minimal always shows a wave.'}
            </div>
            <BannerCropper
              currentUrl={card?.bottom_image_url || null}
              onUpload={handleBottomImageUpload}
              onDelete={card?.bottom_image_url ? handleBottomImageDelete : undefined}
              disabled={!isPremium}
            />
          </fieldset>

          {/* Premium knobs */}
          <fieldset
            className="editor-section"
            style={{
              border: 0, padding: 0, margin: 0, marginBottom: 'var(--sp-6)',
              opacity: (isPremium && !card?.has_custom_layout) ? 1 : 0.5,
            }}
            disabled={!isPremium || card?.has_custom_layout}
          >
            <legend className="editor-section-title">
              Premium adjustments{' '}
              {!isPremium && <span className="muted">— Premium template only</span>}
              {isPremium && card?.has_custom_layout && (
                <span className="muted">— disabled while using a custom layout</span>
              )}
            </legend>

            {/* Photo size */}
            <div className="field">
              <label className="field-label">Photo size</label>
              <div className="display-mode-options" role="radiogroup">
                {PHOTO_SIZES.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    role="radio"
                    aria-checked={form.photo_size === s.key}
                    className="display-mode-option"
                    onClick={() => isPremium && setField('photo_size', s.key)}
                    disabled={!isPremium}
                  >
                    <span className="display-mode-label">{s.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Name font size */}
            <div className="field">
              <label className="field-label">Name font size</label>
              <div className="display-mode-options" role="radiogroup">
                {NAME_FONT_SIZES.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    role="radio"
                    aria-checked={form.name_font_size === s.key}
                    className="display-mode-option"
                    onClick={() => isPremium && setField('name_font_size', s.key)}
                    disabled={!isPremium}
                  >
                    <span className="display-mode-label">{s.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Footer text colour */}
            <div className="field">
              <label className="field-label">Footer text colour</label>
              <div className="display-mode-options" role="radiogroup">
                {FOOTER_TEXT_COLORS.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    role="radio"
                    aria-checked={form.footer_text_color === c.key}
                    className="display-mode-option"
                    onClick={() => isPremium && setField('footer_text_color', c.key)}
                    disabled={!isPremium}
                  >
                    <span className="display-mode-label">{c.label}</span>
                  </button>
                ))}
              </div>
              <div className="field-hint" style={{ marginTop: 'var(--sp-2)' }}>
                Auto picks white or dark based on your brand colour for best contrast.
              </div>
            </div>

            {/* Banner opacity */}
            <div className="field">
              <label className="field-label" htmlFor="f-banner-opacity">
                Banner opacity: {form.banner_opacity}%
              </label>
              <input
                id="f-banner-opacity"
                type="range"
                min="0"
                max="100"
                step="5"
                value={form.banner_opacity}
                onChange={(e) => setField('banner_opacity', Number(e.target.value))}
                disabled={!isPremium}
                style={{ width: '100%' }}
              />
              <div className="field-hint">
                Tone down a busy uploaded banner. 100% is full strength.
                Ignored when using the default mountain range.
              </div>
            </div>
          </fieldset>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 'var(--sp-6)',
            paddingTop: 'var(--sp-5)',
            borderTop: '1px solid var(--line-soft)',
            gap: 'var(--sp-3)',
            flexWrap: 'wrap',
          }}>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleDownloadSample}
              disabled={downloadingSample || dirty}
              title={dirty ? 'Save changes before downloading a sample' : 'Download a print-accurate sample PDF'}
            >
              {downloadingSample ? 'Building…' : 'Download sample PDF'}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || !dirty}
              aria-busy={saving}
            >
              {saving
                ? (<><span className="spinner" aria-hidden="true" /> Saving…</>)
                : 'Save changes'}
            </button>
          </div>
        </form>

        <aside className="branding-preview">
          <div className="preview-label">Preview — ID card</div>
          <KonvaCardPreview tenant={previewTenant} layout={savedLayout} />
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'var(--sp-3)' }}>
            <CardEditorTrigger
              onOpen={() => setEditorOpen(true)}
              hasCustomLayout={!!card?.has_custom_layout}
            />
          </div>
          <p className="muted" style={{ fontSize: 'var(--fs-xs)', marginTop: 'var(--sp-4)', textAlign: 'center' }}>
            Live preview. Click "Download sample PDF" above to see the
            print-accurate output.
          </p>
        </aside>
      </div>

      <CardEditorModal
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          refresh();
          refreshLayout();
        }}
        tenant={previewTenant}
      />
    </>
  );
}
